import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  createHireCode,
  getHireCodes,
  type HireCodeSummary,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";
import { Input } from "@admin/components/ui/input";
import { Label } from "@admin/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";

export default function HireCodesPage() {
  const [items, setItems] = useState<HireCodeSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [search, setSearch] = useState("");
  const [codeType, setCodeType] = useState<"single" | "multi">("single");
  const [campaignTag, setCampaignTag] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const limit = 50;

  const load = () => {
    setErr(null);
    getHireCodes({
      limit,
      offset,
      status: status ? (status as "active" | "revoked" | "exhausted") : undefined,
      type: type ? (type as "single" | "multi") : undefined,
      search: search.trim() || undefined,
    })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch(() => setErr("Could not load invite codes"));
  };

  useEffect(() => {
    load();
  }, [offset, status, type]);

  const onCreate = async () => {
    setBusy(true);
    setErr(null);
    try {
      await createHireCode({
        codeType,
        campaignTag: campaignTag.trim() || undefined,
        note: note.trim() || undefined,
      });
      setCampaignTag("");
      setNote("");
      setOffset(0);
      load();
    } catch {
      setErr("Could not create code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Hire codes ({total})</h1>
      {err && <p className="text-danger">{err}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Create code</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="code-type">Type</Label>
            <select
              id="code-type"
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
              value={codeType}
              onChange={(e) => setCodeType(e.target.value as "single" | "multi")}
            >
              <option value="single">Single use</option>
              <option value="multi">Multi use</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="campaign">Campaign tag</Label>
            <Input id="campaign" value={campaignTag} onChange={(e) => setCampaignTag(e.target.value)} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="note">Note</Label>
            <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div>
            <Button disabled={busy} onClick={() => void onCreate()}>
              Create
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          value={status}
          onChange={(e) => {
            setOffset(0);
            setStatus(e.target.value);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
          <option value="exhausted">Exhausted</option>
        </select>
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          value={type}
          onChange={(e) => {
            setOffset(0);
            setType(e.target.value);
          }}
        >
          <option value="">All types</option>
          <option value="single">Single</option>
          <option value="multi">Multi</option>
        </select>
        <Input
          placeholder="Search code or note"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Button variant="outline" size="sm" onClick={() => { setOffset(0); load(); }}>
          Search
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-muted-fg">
            <tr>
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Redemptions</th>
              <th className="px-4 py-3 font-medium">Campaign</th>
              <th className="px-4 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link to={`/hire/codes/${row.id}`} className="text-accent hover:underline">
                    {row.code}
                  </Link>
                  <div className="text-xs text-muted-fg">{row.sharePath}</div>
                </td>
                <td className="px-4 py-3 text-muted-fg">{row.codeType}</td>
                <td className="px-4 py-3 text-muted-fg">{row.status}</td>
                <td className="px-4 py-3 text-muted-fg">{row.redemptionCount}</td>
                <td className="px-4 py-3 text-muted-fg">{row.campaignTag ?? "-"}</td>
                <td className="px-4 py-3 text-muted-fg">{formatDate(row.createdAt)}</td>
              </tr>
            ))}
            {items.length === 0 && !err && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-fg">
                  No codes found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}
